import { MaybeNil } from '../../../utils/interfaces';
import { isNil } from 'lodash';
import {
  IBarDimensions,
  ICustomBarLabel,
  ICustomBarLabelPosition,
} from './types';
import { CHART_MARGIN } from '../constants/utils';
import { getTextMetrics } from '../utils';
import { memoizeFunction } from '@fluentui/react';

// how far from the bar edge the text is allowed to start or end
const BAR_PADDING = 2;

// all we need from the label is SVG props to estimate the height of the label
type IInternalLabel = Pick<ICustomBarLabel, 'svgProps'>;

// intermediate label with estimated height
interface IInternalLabelEstimatedHeight extends IInternalLabel {
  estimatedHeightPx: number;
}

// additional properties required to position the label correctly
// they are private for the functions in this file
interface IInternalPositionedBarLabel extends ICustomBarLabelPosition {
  labelFits: boolean;
}

// calculating text dimenstions can be expesive (requires context2d) thus we
// memoize
const getTextMetricsMemoized = memoizeFunction(getTextMetrics);

/**
 * Positions list of labels vertically starting from top.
 *
 * @param y0 - initial Y value of all labels
 * @param marginPx - how much space between each label
 * @param labels - the list of labels to position
 * @param bar - bar dimenstions
 *
 * @returns the label with position properties (IInternalPositionedBarLabel)
 */
const positionVertically = <T extends IInternalLabelEstimatedHeight>(
  y0: number,
  marginPx: number,
  labels: T[],
  bar: IBarDimensions,
): Array<T & IInternalPositionedBarLabel> => {
  const vertDim: Array<T & IInternalPositionedBarLabel> = [];
  for (const label of labels) {
    const height = label.estimatedHeightPx;
    const y1 = y0 + height;
    const within = y0 > bar.y && y1 + marginPx < bar.y + bar.height;
    const above = y1 < bar.y;
    const inChart = y0 > 0;
    const mostlyInsideTheBar = y0 + height / 2 > bar.y; // more than half should be inside,
    const dim: T & IInternalPositionedBarLabel = {
      ...label,
      bar,
      position: {
        y0,
        y1,
      },
      insideBar: mostlyInsideTheBar,
      labelFits: inChart && (within || above),
    };
    vertDim.push(dim);
    y0 = dim.position.y1 + marginPx;
  }
  return vertDim;
};

/**
 * Estimates the height of the label based on SVG props
 *
 * @param label
 */
const estimateHeight = <T extends IInternalLabel>(
  label: T,
): T & IInternalLabelEstimatedHeight => {
  const fontSpec = `${label.svgProps?.fontStyle ?? ''} ${
    label.svgProps?.fontWeight ?? ''
  } ${label.svgProps?.fontSize ?? ''} Geist`;
  const { fontBoundingBoxAscent, fontBoundingBoxDescent } =
    getTextMetricsMemoized(
      '0', // all we need is height
      fontSpec,
    );
  const estimatedHeightPx = fontBoundingBoxAscent - fontBoundingBoxDescent;
  return {
    ...label,
    estimatedHeightPx,
  };
};

/**
 * Calculates the total height of the labels taking into account the margin
 * between them
 *
 * @param labels
 * @param marginPx
 */
const calcTotalHeight = <T extends IInternalLabelEstimatedHeight>(
  labels: T[],
  marginPx: number,
) =>
  labels.reduce((sum, lc) => sum + lc.estimatedHeightPx, 0) +
  (labels.length - 1) * marginPx;

/**
 * Positions all labels inside the bar (starting from the bar top line below)
 *
 * @param bar
 * @param labels
 */
const inside = <T extends IInternalLabel>(bar: IBarDimensions, labels: T[]) => {
  const labelsWithHeight = labels.map(estimateHeight);
  const y0 = bar.y + BAR_PADDING;
  return positionVertically(y0, BAR_PADDING, labelsWithHeight, bar);
};

/**
 * Positions all labels above (outside) the bar (starting in reverese from the
 * bar top line)
 *
 * @param bar
 * @param labels
 */
const above = <T extends IInternalLabel>(bar: IBarDimensions, labels: T[]) => {
  const labelsWithHeight = labels.map(estimateHeight);
  const height = calcTotalHeight(labelsWithHeight, BAR_PADDING);
  const y0 = bar.y - BAR_PADDING - height;
  return positionVertically(y0, BAR_PADDING, labelsWithHeight, bar);
};

/**
 * Half of the labels are inside, other half - outside the bar
 *
 * @param bar
 * @param labels
 */
const center = <T extends IInternalLabel>(bar: IBarDimensions, labels: T[]) => {
  const middle = Math.floor(labels.length / 2);
  return [
    ...above(bar, labels.slice(0, middle)),
    ...inside(bar, labels.slice(middle)),
  ];
};

/**
 * Returns true if all labels fit. "Fit" means that:
 * - they are fully contained in the bar
 * - they are outside the bar and are not cut off by the chart dimensions
 *
 * @param posLabel
 */
const allLabelsFit = <T extends IInternalPositionedBarLabel>(posLabel: T[]) =>
  posLabel.every((d) => d.labelFits);

/**
 * Returns true if any 2 labels "overlap" (step on) each other
 *
 * @param posLabels
 */
const overlap = (posLabels: IInternalPositionedBarLabel[]) => {
  const posLabelsSorted = [...posLabels].sort(
    (a, b) => a.position.y0 - b.position.y0,
  );
  let prev: IInternalPositionedBarLabel | null = null;
  for (const posLabel of posLabelsSorted) {
    if (prev === null || prev.position.y1 < posLabel.position.y0) {
      prev = posLabel;
      continue;
    }
    return true;
  }

  return false;
};

/**
 * Recharts bar Y coordinate could underflow (become negative or less than chart
 * margins), the excess is truncated at SVG level (using clip path). This function
 * normalizes bar Y and height to represent _visible_ portion of the bar on screen
 *
 * @param bar
 * @return normalized bar
 */
const normalizeBar = (bar: IBarDimensions): IBarDimensions => {
  if (bar.y < CHART_MARGIN.top) {
    return {
      y: CHART_MARGIN.top,
      height: bar.height + bar.y - CHART_MARGIN.top,
      x: bar.x,
      width: bar.width,
    };
  } else {
    return bar;
  }
};

/**
 * How we want to position one set of labels: in order of preference
 */
const singleGroupOfLabelsStrategy = [center, above, inside];

// when there is just one label, it doesn't look good centered, this
// we have special preference for just one label
const singleLabelStrategy = [above, center, inside];

/**
 * Positions a group labels around a single bar
 *
 * @param bar
 * @param label
 */
export const arrangeSingleGroupLabels = <T extends IInternalLabel>(
  bar: IBarDimensions,
  label: T[],
): Array<T & ICustomBarLabelPosition> => {
  const normalizedBar = normalizeBar(bar);
  const strategy =
    label.length > 1 ? singleGroupOfLabelsStrategy : singleLabelStrategy;
  for (const currStrategy of strategy) {
    const positions = currStrategy(normalizedBar, label);
    if (allLabelsFit(positions)) {
      return positions;
    }
  }
  return [];
};

/**
 * How we want to position the labels in Funnel: in order of preference
 */
const bounceAndConversionStrategy = [
  // [bounce, conversion]
  [center, center],
  [center, above],
  [center, inside],
  [above, center],
  [above, above],
  [above, inside],
];

/**
 * Funnel: positions 2 groups of labels around the conversion and bounce bars
 * in way that they don't overlap. Falls back to conversion labels only
 * if there is not enough room to display both
 *
 * @param conversionBar
 * @param conversionLabels
 * @param bounceBar
 * @param bounceLabels
 */
export const arrangeFunnelLabels = <T extends IInternalLabel>(
  conversionBar: MaybeNil<IBarDimensions>,
  conversionLabels: MaybeNil<T[]>,
  bounceBar: MaybeNil<IBarDimensions>,
  bounceLabels: MaybeNil<T[]>,
): Array<T & ICustomBarLabelPosition> => {
  type IStrategyArgs = [IBarDimensions, T[]];
  const labelGroups: { bounce?: IStrategyArgs; conversion?: IStrategyArgs } =
    {};
  if (!isNil(bounceBar) && !isNil(bounceLabels) && bounceLabels.length > 0) {
    labelGroups.bounce = [normalizeBar(bounceBar), bounceLabels];
  }
  if (
    !isNil(conversionBar) &&
    !isNil(conversionLabels) &&
    conversionLabels.length > 0
  ) {
    labelGroups.conversion = [normalizeBar(conversionBar), conversionLabels];
  }

  // both bounce and conversion labels are supposed to be displayed
  if (!isNil(labelGroups.conversion) && !isNil(labelGroups.bounce)) {
    // try first if we can show both bounce and conversion
    for (const [
      bounceStrategy,
      conversionStrategy,
    ] of bounceAndConversionStrategy) {
      const positions = [
        ...bounceStrategy(...labelGroups.bounce),
        ...conversionStrategy(...labelGroups.conversion),
      ];
      if (allLabelsFit(positions) && !overlap(positions)) {
        return positions;
      }
    }
  }

  // try conversion only
  if (!isNil(labelGroups.conversion)) {
    const conversionLabels = arrangeSingleGroupLabels(
      ...labelGroups.conversion,
    );
    if (conversionLabels.length > 0) {
      return conversionLabels;
    }
  }

  // try bounce only
  if (!isNil(labelGroups.bounce)) {
    const bounceLabels = arrangeSingleGroupLabels(...labelGroups.bounce);
    if (bounceLabels.length > 0) {
      return bounceLabels;
    }
  }

  return [];
};
